import type { GridApi } from 'ag-grid-enterprise';

/**
 * DS localStorage key format — must match `getColumnStateStorageKey` in
 * (Table.columnState.js).
 *
 * Verified against: `const u = (e) => \`naut-table-cols:${e}\``
 */
const columnStateKey = (tableId: string) => `naut-table-cols:${tableId}`;

/**
 * Synchronously flushes the current AG Grid column state to localStorage
 * using the same key the design system reads on remount.
 *
 * WHY THIS IS NEEDED
 * The design system debounces column-state saves by 200 ms and cancels the
 * pending timer during component unmount (React effect cleanup). If the user
 * resizes a column and navigates away before the 200 ms debounce fires, the
 * change is silently dropped and the grid resets to defaults on next visit.
 *
 * AG Grid fires `onGridPreDestroyed` synchronously before `api.destroy()` —
 * which is before the DS debounce cleanup runs — so saving here guarantees
 * the state reaches localStorage on every navigation.
 *
 * @example
 * <Table
 *   tableId="studies-table"
 *   onGridPreDestroyed={(e) => flushColumnState(e.api, 'studies-table')}
 * />
 */
export function flushColumnState(api: GridApi, tableId: string): void {
  try {
    const state = api.getColumnState();
    localStorage.setItem(columnStateKey(tableId), JSON.stringify(state));
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.)
  }
}

/**
 * Sizes all visible columns to fill the available grid width on the very
 * first visit (no persisted column state in localStorage). The resulting
 * state is immediately flushed so the design system restores it on every
 * subsequent visit — and its auto-fit suppression kicks in to prevent
 * the grid from fighting user-customised widths thereafter.
 *
 * Safe to call unconditionally in `onGridReady`: it is a no-op when a
 * saved column state already exists for the given tableId.
 */
export function sizeColumnsOnFirstVisit(api: GridApi, tableId: string): void {
  try {
    if (localStorage.getItem(columnStateKey(tableId)) !== null) return;
    api.sizeColumnsToFit();
    flushColumnState(api, tableId);
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.)
  }
}
